import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { CHAT_DETAIL_ENDPOINT, CHAT_DOMAIN, chatApi } from "api/chatApi";
import { getStringSet, SendBirdProvider } from "@sendbird/uikit-react";
import { userAtom } from "../recoil/userAtom";
import useIsInappWebview from "../hooks/useIsInappWebview";
import koLocale from "date-fns/locale/ko";
import { addAlpha } from "../styles/hexOpacity";
import { StringSet } from "@sendbird/uikit-react/types/ui/Label/stringSet";

const colorSet = {
  "--sendbird-light-primary-100": addAlpha("#FFFFFF", 0.85),
  "--sendbird-light-primary-200": addAlpha("#0F55A7", 0.4),
  "--sendbird-light-primary-300": "#0F55A7",
  "--sendbird-light-primary-400": addAlpha("#0F55A7", 0.4),
  "--sendbird-light-primary-500": addAlpha("#0F55A7", 0.5),
  "--sendbird-light-background-50": "#F6F9FA",
  "--sendbird-light-background-100": addAlpha("#FFFFFF", 0.85),
};

// LightUIKitTheme.palette = {
//   ...Palette,
//   primary100: customTheme.light.card, //코치 말풍선 longpress
//   primary200: addAlpha(customTheme.light.primary, 0.4),
//   primary300: customTheme.light.primary, //내 말풍선
//   primary400: addAlpha(customTheme.light.primary, 0.4),
//   primary500: addAlpha(customTheme.light.primary, 0.5),
//
//   secondary100: customTheme.light.secondary,
//   secondary200: customTheme.light.secondary,
//   secondary300: customTheme.light.secondary,
//   secondary400: customTheme.light.secondary,
//   secondary500: customTheme.light.secondary,
//
//   background50: customTheme.light.background1, //배경
//   background100: customTheme.light.card, //코치 말풍선
//
//   // onBackgroundLight02: "rgba(0,0,0,0)", //코치 닉네임
// };
// export const StringSetKo = {
//   ...RawStringSetKo,
//   LABELS: {
//     ...RawStringSetKo.LABELS,
//     USER_NO_NAME: "(이름없음)",
//     TYPING_INDICATOR_TYPINGS: (users: any[]) => {
//       if (users.length === 0) return "";
//       return `${users.length}명이 입력중...`;
//     },
//     CHANNEL_INPUT_PLACEHOLDER_ACTIVE: "메시지를 입력하세요",
//     CHANNEL_MESSAGE_COPY: "복사",
//     CHANNEL_INPUT_EDIT_OK: "저장",
//     CHANNEL_INPUT_EDIT_CANCEL: "취소",
//     CHANNEL_MESSAGE_EDIT: "수정",
//     CHANNEL_MESSAGE_DELETE: "삭제",
//     CHANNEL_MESSAGE_DELETE_CONFIRM_TITLE: "메시지를 삭제하시겠습니까?",
//     CHANNEL_MESSAGE_DELETE_CONFIRM_OK: "삭제",
//     CHANNEL_MESSAGE_DELETE_CONFIRM_CANCEL: "취소",
//   },
//   PLACEHOLDER: {
//     ...RawStringSetKo.PLACEHOLDER,
//     NO_MESSAGES: "코치 배정 중...",
//     ERROR: { RETRY_LABEL: "재시도" },
//   },
//   GROUP_CHANNEL: { ...RawStringSetKo.GROUP_CHANNEL, MESSAGE_BUBBLE_EDITED_POSTFIX: "(수정됨)" },
//   TOAST: { ...RawStringSetKo.TOAST, COPY_OK: "복사됨" },
//   dateLocale: koLocale,
// };
//
// export const StringSetEn = {
//   ...RawStringSetEn,
//   PLACEHOLDER: {
//     ...RawStringSetEn.PLACEHOLDER,
//     NO_MESSAGES: "Assigning your coach...",
//   },
//   dateLocale: enLocale,
// };

export const koStringSet: StringSet = {
  ...getStringSet(),

  NO_NAME: "(이름없음)",
  MESSAGE_STATUS__YESTERDAY: "어제",
  CHANNEL__MESSAGE_LIST__NOTIFICATION__NEW_MESSAGE: "개의 새로운 메시지",
  CHANNEL__MESSAGE_LIST__NOTIFICATION__ON: "",

  MESSAGE_INPUT__PLACE_HOLDER: "메시지를 입력하세요",
  TRYING_TO_CONNECT: "연결 중...",

  CONTEXT_MENU_DROPDOWN__COPY: "복사",
  CONTEXT_MENU_DROPDOWN__EDIT: "수정",
  CONTEXT_MENU_DROPDOWN__RESEND: "재전송",
  CONTEXT_MENU_DROPDOWN__DELETE: "삭제",
  CONTEXT_MENU_DROPDOWN__SAVE: "저장",

  MESSAGE_MENU__COPY: "복사",
  MESSAGE_MENU__REPLY: "답변",
  MESSAGE_MENU__EDIT: "수정",
  MESSAGE_MENU__THREAD: "쓰레드",
  MESSAGE_MENU__RESEND: "재전송",
  MESSAGE_MENU__DELETE: "삭제",
  MESSAGE_MENU__SAVE: "저장",

  PLACE_HOLDER__RETRY_TO_CONNECT: "재시도",

  MESSAGE_EDITED: "(수정됨)",
};

export function SendbirdWrapper({ children }: { children: React.ReactNode }) {
  const [stringSet] = useState({
    ...koStringSet,
  });

  const isInappWebview = useIsInappWebview();
  const userState = useRecoilValue(userAtom);
  const { data, isLoading, error } = useQuery(
    [CHAT_DOMAIN, CHAT_DETAIL_ENDPOINT, userState?.userId],
    () => chatApi.getDetail(),
    {
      retry: false,
      enabled: !!userState?.userId && userState?.role === "patient",
    }
  );

  if (userState?.role !== "patient" || isInappWebview) return <>{children}</>;
  if (isLoading) return <>{children}</>;
  if (error || !data?.result?.chatConnection) return <>{children}</>;

  try {
    const { userId: sendbirdUserId, userAccessToken: sendbirdAccessToken } =
      data.result.chatConnection;

    return (
      <SendBirdProvider
        appId={process.env.REACT_APP_SENDBIRD_APP_ID!}
        userId={sendbirdUserId}
        accessToken={sendbirdAccessToken}
        stringSet={stringSet}
        colorSet={colorSet}
        dateLocale={koLocale}
        isMultipleFilesMessageEnabled={false}
      >
        {children}
      </SendBirdProvider>
    );
  } catch (err) {
    console.error("Error initializing SendBird:", err);
    return <>{children}</>;
  }
}
